var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import { baseClientDefaults } from '@juvare/platform-sdk-js/BaseClient';
import axios from 'axios';
export class AlertBaseClient {
    constructor(tokenProvider, baseUrl) {
        /**
         * This function does nothing, do not use it. Currently, it is not possible to cancel HTTP requests.
         * @deprecated
         */
        this.abort = () => {
            console.warn('BaseClient.abort function is deprecated, please update code to not call it');
        };
        baseUrl !== null && baseUrl !== void 0 ? baseUrl : (baseUrl = baseClientDefaults.baseUrl);
        this.axiosInstance = axios.create({
            baseURL: baseUrl,
        });
        // This fires events on each call to platform server. These events are needed to prevent
        // "Your session will expire in..." message when user is not actively doing anything, but some long-running
        // tasks are happening in the background, such as bulk actions, file uploads, etc.
        // A React hook "useIdleTimeout" in common-web-ui project listens to these events.
        this.axiosInstance.interceptors.request.use((config) => {
            safeDispatchPlatformApiWindowEvent();
            return config;
        }, (error) => {
            safeDispatchPlatformApiWindowEvent();
            return Promise.reject(error);
        });
        const retryRequest = (config) => __awaiter(this, void 0, void 0, function* () {
            config.retry -= 1;
            yield new Promise((resolve) => setTimeout(resolve, config.retryDelay || 1000));
            return this.axiosInstance(config);
        });
        if (tokenProvider) {
            this.axiosInstance.interceptors.request.use((config) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                if (((_a = config.url) === null || _a === void 0 ? void 0 : _a.startsWith('/public-data')) || ((_b = config.url) === null || _b === void 0 ? void 0 : _b.startsWith('/api/v1/anonymous'))) {
                    return config;
                }
                if (tokenProvider) {
                    const token = yield getToken(tokenProvider);
                    config.headers['Authorization'] = 'Bearer ' + token;
                }
                return config;
            }));
            this.axiosInstance.interceptors.response.use(undefined, (error) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const { config } = error;
                if (axios.isAxiosError(error) &&
                    ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 401 &&
                    tokenProvider &&
                    config &&
                    !config.retryAfterRefreshToken) {
                    yield refreshToken(tokenProvider);
                    config.retryAfterRefreshToken = true;
                    return this.axiosInstance(config);
                }
                if (!config || !config.retry) {
                    throw createClientError(error);
                }
                return retryRequest.call(this, config);
            }));
        }
        else {
            this.axiosInstance.interceptors.response.use(undefined, (error) => {
                const { config } = error;
                if (!config || !config.retry) {
                    return Promise.reject(createClientError(error));
                }
                return retryRequest.call(this, config);
            });
        }
    }
}
const state = {};
export function getToken(tokenProvider) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!state.token) {
            const token = yield tokenProvider.onTokenRequest();
            state.token = token;
            return token;
        }
        return state.token;
    });
}
export function refreshToken(tokenProvider) {
    return __awaiter(this, void 0, void 0, function* () {
        const token = yield tokenProvider.onTokenRequest();
        state.token = token;
        return token;
    });
}
export function resetToken() {
    state.token = undefined;
}
export class ClientError extends Error {
    constructor(message, status, type, data, cause) {
        super(message);
        this.message = message;
        this.status = status;
        this.type = type;
        this.data = data;
        this.cause = cause;
    }
}
function createClientError(error) {
    if (axios.isAxiosError(error)) {
        if (error.response) {
            let contentType = error.response.headers['content-type'];
            if (contentType) {
                contentType = contentType.split(';')[0].trim().toLowerCase();
            }
            if (contentType === 'application/json') {
                return new ClientError(getObjectField(error.response.data, 'message', 'string', 'Unknown JSON error response was returned from server'), error.response.status, getObjectField(error.response.data, 'type', 'string', 'unknown-error-json'), getObjectField(error.response.data, 'data', 'object', undefined), error);
            }
            else if (contentType === 'text/html') {
                return new ClientError('Unknown HTML error response was returned from server', error.response.status, getObjectField(error.response.data, 'type', 'string', 'unknown-error-html'), { html: error.response.data }, error);
            }
            else if (contentType === 'text/plain') {
                return new ClientError('Unknown text error response was returned from server', error.response.status, getObjectField(error.response.data, 'type', 'string', 'unknown-error-text'), { text: error.response.data }, error);
            }
            else {
                return new ClientError('Unknown error response was returned from server', error.response.status, getObjectField(error.response.data, 'type', 'string', 'unknown-error-type'), undefined, error);
            }
        }
        else if (error.request) {
            if (error.code === 'ECONNREFUSED') {
                return new ClientError('Failed to make a request to the server, check that URL is correct and the server is running', -1, 'no-response', undefined, error);
            }
            else if (error.code === 'EPROTO') {
                return new ClientError('Failed to make a request to the server, check that you are not making a HTTPS call to an HTTP endpoint', -1, 'no-response', undefined, error);
            }
            else {
                return new ClientError('Failed to make a request to the server', -1, 'no-response', undefined, error);
            }
        }
        else {
            return new ClientError('Failed to create request', -1, 'no-request', undefined, error);
        }
    }
    else if (axios.isCancel(error)) {
        return new ClientError('Request was canceled', -1, 'request-canceled', undefined, error);
    }
    else {
        return new ClientError('Unknown error', -1, 'unknown-error', undefined, error);
    }
}
function getObjectField(object, field, type, defaultValue) {
    if (typeof object === 'object' && object != null && typeof object[field] === type && object[field] != null) {
        return object[field];
    }
    else {
        return defaultValue;
    }
}
function safeDispatchPlatformApiWindowEvent() {
    // Because "platform-sdk-js" library needs to work both in browser and in node, we cannot rely on global window
    // object to be present or TypeScript compiler to have a definition for it.
    const { window, Event } = globalThis;
    if (window && window.dispatchEvent && Event) {
        window.dispatchEvent(new Event('platform-api'));
    }
}
